/**
 * MobileContentDisplay — a mobile-safe version of ContentDisplay.
 * Skips gaussian splats (SplatMesh/SparkRenderer/GaussianSplats3D) which crash
 * WebGL on many mobile devices. Only renders images and GLB models.
 * PLY and splat content types are replaced with a placeholder.
 */
import React, { useRef, Suspense } from 'react';
import { Image as DreiImage, useGLTF } from '@react-three/drei';
import * as THREE from 'three';

interface ContentItem {
  id: string;
  url: string;
  type: 'image' | 'audio' | 'model' | 'splat' | 'ply';
  position: [number, number, number];
  scale: number;
  rotation: [number, number, number];
  isLocked?: boolean;
}

interface MobileContentDisplayProps {
  content: ContentItem;
  isSelected: boolean;
  onSelect: () => void;
}

const MobileModelContent: React.FC<{ url: string; onSelect: () => void }> = ({ url, onSelect }) => {
  const { scene } = useGLTF(url);
  return <primitive object={scene.clone()} onClick={onSelect} />;
};

const MobileContentDisplay: React.FC<MobileContentDisplayProps> = ({ content, isSelected, onSelect }) => {
  const groupRef = useRef<THREE.Group>(null!);
  const s = content.scale;

  let inner: React.ReactNode = null;

  if (content.type === 'image') {
    inner = <DreiImage url={content.url} scale={2} onClick={onSelect} transparent />;
  } else if (content.type === 'model') {
    inner = (
      <Suspense fallback={<mesh><boxGeometry args={[0.5, 0.5, 0.5]} /><meshBasicMaterial color="#00c2b3" wireframe /></mesh>}>
        <MobileModelContent url={content.url} onSelect={onSelect} />
      </Suspense>
    );
  } else if (content.type === 'splat' || content.type === 'ply') {
    // Skip gaussian splats and PLY on mobile — show a placeholder sphere
    inner = (
      <mesh onClick={onSelect}>
        <sphereGeometry args={[0.5, 16, 16]} />
        <meshBasicMaterial color="#00c2b3" wireframe transparent opacity={0.4} />
      </mesh>
    );
  } else if (content.type === 'audio') {
    inner = (
      <mesh onClick={onSelect}>
        <sphereGeometry args={[0.5, 16, 16]} />
        <meshBasicMaterial color="#00E5FF" wireframe={isSelected} />
      </mesh>
    );
  }

  if (!inner) return null;

  return (
    <group
      ref={groupRef}
      position={content.position}
      rotation={content.rotation}
      scale={[s, s, s]}
    >
      {inner}
    </group>
  );
};

export default MobileContentDisplay;
